import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { GameInput } from '../controls'
import { currentTier } from '../evolution'
import { T } from '../tunables'

// ── Steering (WASD+cursor — winner of the amoeba-controls.md shootout) ───────
// Mutates player.velocity only; the caller integrates position.

// The move vector steers the body; the cursor is a free aim reticle. Analog
// magnitude scales speed (keys give unit vectors, sticks give 0..1).
function steerPlayer(player: Entity, input: GameInput, dt: number, speedMult: number): void {
  const dir = input.actions.vec('move')
  const c = T.controls.wasd
  if (dir.x !== 0 || dir.y !== 0) {
    const max = c.maxSpeed * speedMult
    const blend = Math.min(1, c.accel * dt)
    player.velocity!.x += (dir.x * max - player.velocity!.x) * blend
    player.velocity!.y += (dir.y * max - player.velocity!.y) * blend
  } else {
    player.velocity!.x -= player.velocity!.x * c.stopDamping * dt
    player.velocity!.y -= player.velocity!.y * c.stopDamping * dt
  }
  capSpeed(player, c.maxSpeed * speedMult)
}

function capSpeed(player: Entity, maxSpeed: number): void {
  const speed = Math.hypot(player.velocity!.x, player.velocity!.y)
  if (speed > maxSpeed) {
    const s = maxSpeed / speed
    player.velocity!.x *= s
    player.velocity!.y *= s
  }
}

export function movementSystem(world: GameWorld, player: Entity, input: GameInput, dt: number, speedMult = 1) {
  // Microbes: simple drift
  for (const e of world.with('position', 'velocity')) {
    if (e.player || e.absorbing) continue
    e.position.x += e.velocity.x * dt
    e.position.y += e.velocity.y * dt
  }

  if (player.velocity && player.position) {
    const engulfActive = (player.engulf?.activeRemaining ?? 0) > 0
    // Spore-cloud slow: linger timer decays; movement is throttled while > 0.
    if (player.slowRemaining !== undefined && player.slowRemaining > 0) {
      player.slowRemaining = Math.max(0, player.slowRemaining - dt)
    }
    // Tier-3+ specimens have adapted to the spore floor — a gentler drag.
    const sporeSlow = currentTier(player) >= T.spore.resilientTier
      ? T.spore.resilientSlowMult : T.spore.slowMult
    const slowMult = (player.slowRemaining ?? 0) > 0 ? sporeSlow : 1
    const effectiveSpeedMult = speedMult * (engulfActive ? T.growth.engulfSpeedMult : 1) * slowMult
    // Dash override: use dash impulse while active
    if (player.dash && player.dash.activeRemaining > 0) {
      player.velocity.x = player.dash.impulse.x
      player.velocity.y = player.dash.impulse.y
    } else if (!input.focused) {
      // When unfocused, coast to stop: just damp velocity, ignore stale cursor.
      const damping = 5
      player.velocity.x -= player.velocity.x * damping * dt
      player.velocity.y -= player.velocity.y * damping * dt
    } else {
      steerPlayer(player, input, dt, effectiveSpeedMult)
    }
    player.position.x += player.velocity.x * dt
    player.position.y += player.velocity.y * dt
  }

  // Wobble phase advances for all wobbling entities
  for (const e of world.with('wobble')) {
    e.wobble.phase += e.wobble.freq * dt
  }
}
